import { ForwardedRef, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import IOSVideoPlayer from '@components/Video/IOSVideoPlayer';
import YouTubePlayer from '@components/Video/YouTubePlayer';
import { getSizesString } from '@utils/misc';
import { IParsedImage, IVideoType } from '@components/typescript';
import styles from '@components/Video/VideoPlayer.styles';
import getVideoUrl from '@utils/getVideoUrl';
import ImageSource from '@components/ImageSource/ImageSource';
import Loading from '@components/Loading/Loading';
import { useResponsive } from '@features/responsive';
import getImageQuality from '@utils/getImageQuality';
import useImageSources from '@hooks/useImageSources';
import getCurrentDevice from '@utils/getCurrentDevice';
type PropsType = {
  video: IVideoType | null;
  playVideo: boolean;
  withSound?: boolean;
  isEndFunc?: () => void;
  poster?: string | IParsedImage;
  controls?: boolean;
  loop?: boolean;
  onLoaded?: () => void;
  fitVideo?: boolean;
  priority?: boolean;
  onErrorFunc?: () => void;
  fullscreen?: boolean;
};

// Funkcja do wykrywania urządzeń iOS
const isIOS = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as Window & {
    MSStream?: unknown;
  }).MSStream;
};

// Funkcja do sprawdzania czy to YouTube
const isYouTube = (videoUrl: string) => {
  if (videoUrl.startsWith('http')) {
    return videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be');
  }
  return false;
};
const VideoPlayer = forwardRef(({
  video,
  playVideo,
  withSound = false,
  isEndFunc = undefined,
  poster = undefined,
  controls = false,
  loop = false,
  onLoaded = undefined,
  fitVideo = false,
  priority = false,
  onErrorFunc,
  fullscreen = false
}: PropsType, ref?: ForwardedRef<HTMLDivElement>) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(playVideo);
  const videoRef = useRef<HTMLVideoElement>(null);
  const {
    currentBreakpoint
  } = useResponsive();
  let imageSources: IParsedImage = {
    image: {
      mobile: {
        path: '',
        metadata: {
          alt: ''
        }
      },
      tablet: {
        path: '',
        metadata: {
          alt: ''
        }
      },
      desktop: {
        path: '',
        metadata: {
          alt: ''
        }
      }
    }
  };
  if (poster) {
    imageSources = typeof poster === 'string' ? {
      image: {
        mobile: {
          path: poster,
          metadata: {
            alt: ''
          }
        },
        tablet: {
          path: poster,
          metadata: {
            alt: ''
          }
        },
        desktop: {
          path: poster,
          metadata: {
            alt: ''
          }
        }
      }
    } : poster;
  }
  const {
    getSrcs
  } = useImageSources(imageSources);
  const {
    imageSrc: posterImg
  } = getSrcs(getCurrentDevice(currentBreakpoint));
  const currentVideoUrl = useMemo(() => {
    let newCurrentVideoUrl: string | undefined;
    if (video) {
      newCurrentVideoUrl = getVideoUrl(video, currentBreakpoint);
      if (!newCurrentVideoUrl) {
        setIsLoading(false);
        if (onErrorFunc) onErrorFunc();
      }
    }
    return newCurrentVideoUrl || undefined;
  }, [video, currentBreakpoint, onErrorFunc]);
  const playVideoHandler = useCallback(async () => {
    if (isYouTube(currentVideoUrl || '')) {
      setIsPlaying(true);
    } else if (videoRef.current) {
      try {
        if (videoRef.current.readyState >= 2 && videoRef.current.paused) {
          await videoRef.current.play();
          setIsPlaying(true);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error playing video:', error);
        }
      }
    }
  }, [currentVideoUrl]);
  const stopVideo = useCallback(() => {
    if (isYouTube(currentVideoUrl || '')) {
      setIsPlaying(false);
    } else if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, [currentVideoUrl]);
  const handleVideoReady = () => {
    setIsLoading(false);
    if (onLoaded) onLoaded();
    if (playVideo) {
      playVideoHandler();
    }
  };
  const handleIframeLoad = () => {
    setIsLoading(false);
    if (onLoaded) onLoaded();
    if (playVideo) {
      setIsPlaying(true);
    }
  };
  const handleEndVideo = () => {
    setIsPlaying(false);
    if (isEndFunc) isEndFunc();
  };
  const handleError = () => {
    setIsLoading(false);
    if (onErrorFunc) onErrorFunc();
  };
  useEffect(() => {
    if (playVideo) {
      playVideoHandler();
    } else {
      stopVideo();
    }
  }, [playVideo, playVideoHandler, stopVideo]);
  const Poster = typeof poster === 'string' ? <Image src={poster} alt="" fill sx={styles.thumbnail} quality={getImageQuality()} sizes={getSizesString(['100vw', '100vw', '75vw', '75vw', '50vw'])} priority={priority} /> : <ImageSource imageSources={poster as IParsedImage} sizes={getSizesString(['100vw', '100vw', '75vw', '75vw', '50vw'])} alt="" priority={priority} />;

  // Renderowanie dla iOS
  if (isIOS()) {
    return <div sx={styles.videoContainer} ref={ref}>
          <IOSVideoPlayer video={currentVideoUrl || ''} loop={loop} poster={typeof posterImg === 'string' ? posterImg : posterImg?.path || ''} play={playVideo} onLoaded={onLoaded} onEnded={handleEndVideo} onError={handleError} withSound={withSound} controls={controls} fitVideo={fitVideo} fullscreen={fullscreen} />
        </div>;
  }

  // Renderowanie dla YouTube
  if (currentVideoUrl && isYouTube(currentVideoUrl)) {
    return <div sx={styles.videoContainer} ref={ref}>
          <YouTubePlayer video={currentVideoUrl} isPlaying={isPlaying} isLoading={isLoading} loop={loop} onPlay={playVideoHandler} onLoad={handleIframeLoad} onEnded={handleEndVideo} controls={controls} />
        </div>;
  }

  // Renderowanie standardowego video
  return <div sx={styles.videoContainer} ref={ref}>
        {isLoading && (poster ? Poster : <Loading />)}
        <video ref={videoRef} src={currentVideoUrl} muted={!withSound} loop={loop} playsInline preload="metadata" webkit-playsinline="true" x5-playsinline="true" x-webkit-airplay="allow" controls={controls} disablePictureInPicture={!fullscreen} sx={styles.videoStyles(fullscreen, fitVideo)} onLoadedData={handleVideoReady} onEnded={handleEndVideo} onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} onError={handleError} onContextMenu={e => e.preventDefault()} />
      </div>;
});
VideoPlayer.displayName = 'VideoPlayer';
export default VideoPlayer;